/** How a date is written: `auto` (the habit of the person's language) or a fixed order of numbers: `dmy` 08/10/2026, `mdy` 10/08/2026, `ymd` 2026-10-08. */
export type DateFormat = "auto" | "dmy" | "mdy" | "ymd";

/** How a time is written: `auto` (the habit of the person's language), `24h` (14:05) or `12h` (2:05 PM). */
export type TimeFormat = "auto" | "24h" | "12h";

/** Everything needed to write a moment the way a person wants to read it: their language, their time zone and their date and time habits. */
export type DisplayFormat = { locale: string; timezone: string; dateFormat: DateFormat; timeFormat: TimeFormat };

/** Plain "en" would write dates the American way (month first); this is a European site, so English means en-GB. */
const tag = (locale: string) => (locale === "en" ? "en-GB" : locale);

const ORDER = { dmy: ["day", "month", "year"], mdy: ["month", "day", "year"], ymd: ["year", "month", "day"] } as const;

function datePart(date: Date, display: DisplayFormat, style: "medium" | "long" = "medium"): string {
  const { locale, timezone, dateFormat } = display;
  if (dateFormat === "auto") return new Intl.DateTimeFormat(tag(locale), { dateStyle: style, timeZone: timezone }).format(date);
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: timezone }).formatToParts(date).map((p) => [p.type, p.value]));
  return ORDER[dateFormat].map((k) => parts[k]).join(dateFormat === "ymd" ? "-" : "/");
}

function timePart(date: Date, display: DisplayFormat): string {
  const { locale, timezone, timeFormat } = display;
  const hour = timeFormat === "auto" ? {} : { hourCycle: timeFormat === "24h" ? ("h23" as const) : ("h12" as const) };
  return new Intl.DateTimeFormat(tag(locale), { hour: timeFormat === "24h" ? "2-digit" : "numeric", minute: "2-digit", timeZone: timezone, ...hour }).format(date);
}

/** A moment written as the person wants (`display`): just the date, just the time, or both, in their time zone. Numeric dates (dmy, mdy, ymd) do not depend on the language; `auto` follows the language's habit. */
export function formatMoment(value: Date | number, display: DisplayFormat, kind: "date" | "dateLong" | "time" | "dateTime"): string {
  const date = new Date(value);
  if (kind === "date") return datePart(date, display);
  if (kind === "dateLong") return datePart(date, display, "long");
  if (kind === "time") return timePart(date, display);
  return `${datePart(date, display)} ${timePart(date, display)}`;
}

